"use client";

import React, { useEffect, useRef, useState } from "react";
import Footer from "../Footer/Footer";
import TopBar from "../Header/TopBar";
import Header from "../Header/Header";
import ScrollToTop from "@/utils/ScrollToTop";
import { usePathname } from "next/navigation";
import { ThemeProvider } from "@/contexts/ThemeContext";
import ReduxProvider from "../Providers/ReduxProvider";

const RootLayoutWrapper = ({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) => {
  const pathname = usePathname();
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const lastScrollYRef = useRef(0);
  const suppressStickyRef = useRef(false);
  const [scrolled, setScrolled] = useState(false);
  const suppressTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (pathname === "/affiliate") return;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (suppressStickyRef.current) {
        setShowStickyHeader(false);
        lastScrollYRef.current = currentScrollY;
        return;
      }

      if (currentScrollY <= 80) {
        setShowStickyHeader(false);
      } else if (currentScrollY < lastScrollYRef.current) {
        setShowStickyHeader(true);
      } else {
        setShowStickyHeader(false);
      }

      lastScrollYRef.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [pathname]);

  useEffect(() => {
    const scrollToHashSection = () => {
      const hash = window.location.hash;
      if (!hash) return;

      const section = document.querySelector(hash);
      if (!section) return;

      suppressStickyRef.current = true;
      setShowStickyHeader(false);
      if (suppressTimeoutRef.current) {
        clearTimeout(suppressTimeoutRef.current);
      }

      requestAnimationFrame(() => {
        const headerOffset = 0;
        const sectionTop =
          section.getBoundingClientRect().top + window.scrollY - headerOffset;

        window.scrollTo({
          top: Math.max(sectionTop, 0),
          behavior: "smooth",
        });

        suppressTimeoutRef.current = setTimeout(() => {
          suppressStickyRef.current = false;
        }, 900);
      });
    };

    scrollToHashSection();
    window.addEventListener("hashchange", scrollToHashSection);

    return () => {
      window.removeEventListener("hashchange", scrollToHashSection);
      if (suppressTimeoutRef.current) {
        clearTimeout(suppressTimeoutRef.current);
      }
    };
  }, [pathname]);

  return (
    <ReduxProvider>
      <ThemeProvider>
        {/* <ScrollToTop /> */}
        {pathname === "/affiliate" ? null : (
          <>
            <div
              className={`z-[999] transition-all duration-300 ${
                showStickyHeader
                  ? "fixed top-0 left-0 right-0 shadow-lg"
                  : "fixed top-0 left-0 right-0 lg:relative"
              }`}
            >
              {/* Hide TopBar on scroll: threshold of 40px (height of TopBar) */}
              <div 
                className={`transition-all duration-300 overflow-hidden ${
                  showStickyHeader ? "hidden lg:block" : "block"
                } ${scrolled ? "max-h-0 opacity-0" : "max-h-10 opacity-100"}`}
              >
                <TopBar />
              </div>
              <Header />
            </div>

            {/* Spacer for mobile: always needed since header is fixed top-0 */}
            <div className="h-[104px] lg:hidden" aria-hidden="true" />

            {/* Spacer for desktop: only needed when header becomes fixed/sticky */}
            {showStickyHeader && (
              <div className="hidden lg:block h-23" aria-hidden="true" />
            )}
          </>
        )}

        {children}
        {pathname === "/affiliate" ? null : <Footer />}
      </ThemeProvider>
    </ReduxProvider>
  );
};

export default RootLayoutWrapper;
